import "./JobInvoiceSendDrawer.css";
import GrowthSignal from "../../../../components/growth/GrowthSignal";
import { useEffect, useRef, useState } from "react";
import { hasRole, useHub } from "../../../../core/Global";
import { API_ROUTES } from "../../../../core/Constants";
import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import Form from "../../../../components/form/Form";
import Button from "../../../../components/base/Button";
import Icon from "../../../../components/base/Icon";
import TextBox from "../../../../components/form/TextBox";
import PhoneInput from "../../../../components/form/PhoneInput";
import { toastSuccess } from "../../../../components/base/Toast";
import { FontAwesomeIcons } from "../../../../core/Types";
import PhoneItem from "../../../../models/PhoneItem";
import { AddonTypes } from "../../../system/settings/models/UsersAddonModel";

type SendChannel = "email" | "phone" | "wa";

interface IProps {
    /** One or more invoice/estimate ids to send. */
    ids: string[];
    jobId?: string;
    title?: string;
    buttonText?: string;
    defaultEmail?: string;
    defaultPhone?: PhoneItem;
    onClose: () => void;
    onSent?: () => void;
}

const DRAWER_ID = "drawer-jobinvoice-send";

const CHANNELS: { key: SendChannel; label: string; icon: FontAwesomeIcons; iconType?: "solid" | "brands" }[] = [
    { key: "email", label: "Email", icon: "envelope" },
    { key: "phone", label: "SMS", icon: "comment-sms" },
    { key: "wa", label: "WhatsApp", icon: "whatsapp", iconType: "brands" }
];

export default function JobInvoiceSendDrawer(props: IProps) {

    const hub = useHub();
    const drawerRef = useRef<DrawerRef>(null);

    const [sendTo, sendToSet] = useState<SendChannel>("email");
    const [sending, sendingSet] = useState<boolean>(false);
    const [waActive, waActiveSet] = useState<boolean | undefined>(undefined);

    const canManageAddons = hasRole("SystemSettings");

    useEffect(() => {
        hub.Get("/accounts/profile/addons", res => {
            const wa = (res?.data as any[] | undefined)?.find(a => a.addonType === AddonTypes.WhatsApp);
            waActiveSet(!!wa?.active);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openAddons = () => window.open("/system/finance/subscription", "_blank");

    const waBlocked = sendTo === "wa" && waActive === false;

    const submit = (body: any, callback?: (res?: any) => void) => {
        sendingSet(true);
        hub.Post(API_ROUTES.JobInvoices + "sendinvoice", {
            ...body,
            ids: props.ids.join(","),
            sendTo,
            jobId: props.jobId
        }, res => {
            sendingSet(false);
            callback?.(res);
            if (res.success) {
                toastSuccess("Sent successfully!");
                props.onSent?.();
                drawerRef.current?.close();
            }
        });
    };

    return (
        <Drawer ref={drawerRef} id={DRAWER_ID} title={props.title ?? "Send"} onClose={props.onClose}>
            <Form className="pt-3" onSubmit={submit}>

                <div className="fw-bold fs-7 mb-2">Send via</div>
                <div className="d-flex mb-6">
                    {
                        CHANNELS.map(ch =>
                            <label key={ch.key}
                                className={"jis-channel flex-fill d-flex align-items-center justify-content-center me-3 " + (sendTo === ch.key ? "active" : "")}>
                                <input type="radio" className="d-none" checked={sendTo === ch.key}
                                    onChange={e => { if (e.target.checked) sendToSet(ch.key); }} />
                                <Icon icon={ch.icon} type={ch.iconType} size={16} className="me-2" />
                                <span className="fs-7 fw-bold">{ch.label}</span>
                            </label>
                        )
                    }
                </div>

                {
                    sendTo === "email" &&
                    <TextBox id="txt-jis-email" key="txt-jis-email" name="emails" email
                        label="Email Address" labelUp block autoFocus value={props.defaultEmail} />
                }
                {
                    sendTo === "phone" &&
                    <PhoneInput id="txt-jis-sms" key="txt-jis-sms" name="phone" parent={DRAWER_ID}
                        label="Phone Number" labelUp block autoFocus defaultPhone={props.defaultPhone} />
                }
                {
                    sendTo === "wa" && (
                        waActive === false ?
                            <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-5">
                                <Icon icon="triangle-exclamation" type="solid" className="text-warning me-3 mt-1" size={18} />
                                <GrowthSignal kind="paywall" feature="whatsapp" detail="send an invoice by WhatsApp" />
                                <div className="flex-grow-1">
                                    <div className="fw-bold text-gray-800 mb-1">WhatsApp is not active</div>
                                    <div className="fs-7 text-gray-600">
                                        The WhatsApp subscription is not active for this account.
                                    </div>
                                    {
                                        canManageAddons &&
                                        <Button className="mt-3" type="light" color="warning" icon="up-right-from-square"
                                            text="Manage Add-ons" onClick={openAddons} />
                                    }
                                </div>
                            </div>
                            :
                            <PhoneInput id="txt-jis-wa" key="txt-jis-wa" name="phone" parent={DRAWER_ID}
                                label="Phone Number" labelUp block autoFocus defaultPhone={props.defaultPhone} countryCodes="all" />
                    )
                }

                {
                    !waBlocked &&
                    <div className="mt-8">
                        <Button isSubmit large color="success" icon="paper-plane"
                            isRunning={sending} runningText="Sending..."
                            text={props.buttonText ?? "Send"} />
                    </div>
                }

            </Form>
        </Drawer>
    );
}
